// Per-page cause filter (below the session's LE / OU / department). Lives in the URL as
// ?cause=<id> so dashboard → reports links keep it. Lists only causes of the session's OU and,
// when one is selected, department.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useAuth } from "@/lib/auth";
import { fetchCauses, type Cause } from "@/lib/donation";
import { useSessionScope } from "@/lib/session-scope";
import { Combobox } from "@/components/ui/combobox";

/** The ?cause= filter from the URL (null = all causes) and a setter. */
export function useCauseParam(): [number | null, (id: number | null) => void] {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get("cause"));
  const causeId = Number.isFinite(raw) && raw > 0 ? raw : null;
  function setCause(id: number | null) {
    const q = new URLSearchParams(params);
    if (id) q.set("cause", String(id));
    else q.delete("cause");
    setParams(q, { replace: true });
  }
  return [causeId, setCause];
}

export function CauseFilter({ className }: { className?: string }) {
  const { token } = useAuth();
  const { scope } = useSessionScope();
  const [causeId, setCause] = useCauseParam();
  const [causes, setCauses] = useState<Cause[] | null>(null);
  const ouId = scope?.operationUnit.id ?? null;
  const deptId = scope?.department?.id ?? null;

  useEffect(() => {
    setCauses(null);
    if (!token || ouId == null) return;
    const controller = new AbortController();
    fetchCauses(token, ouId, deptId, controller.signal)
      .then(setCauses)
      .catch((e) => {
        if ((e as Error).name !== "AbortError") setCauses([]);
      });
    return () => controller.abort();
  }, [token, ouId, deptId]);

  // id 0 = "All causes" (the Combobox has no clear action).
  const options = useMemo(
    () => [
      { id: 0, name: "All causes" },
      ...(causes ?? []).map((c) => ({ id: Number(c.id), name: c.isActive ? c.name : `${c.name} (inactive)` })),
    ],
    [causes]
  );

  return (
    <div className={className}>
      <Combobox
        options={options}
        value={causeId ?? 0}
        onValueChange={(id) => setCause(id ? id : null)}
        placeholder="All causes"
        emptyText="No causes found."
        disabled={causes == null}
      />
    </div>
  );
}
